'use client';
import { useTranslations } from 'next-intl';
import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import {
  AutoSizer,
  CellMeasurer,
  InfiniteLoader,
  type MasonryCellProps,
  Masonry as VirtualizedMasonry,
  WindowScroller,
} from 'react-virtualized';
import CreateGuide from '@/app/assets/imgs/create_guide.svg';
import { Loader } from '@/app/components/ui/loader';
import { TAB_TYPE_MAP } from '@/app/constants';
import { useExpose, useMasonry } from '@/app/hooks';
import type { LoadingState, NewShowCaseList, PostReferenceItem } from '@/app/types';
import { CommunityEnum } from '@/app/types';
import { cn, getCurrentItem, isRowLoaded, mapTypeToExploreTab, reportEvent } from '@/app/utils';
import { createPositioner } from '@/app/utils/client';
import { usePathname } from '@/i18n/routing';
import type { FeedProps } from '..';
import { PostReferenceCard } from '..';
import { useFeedCard } from '../hooks/use-feed-card';
import { FeedCard } from './FeedCard';
import style from './index.module.scss';

export const FeedList: FC<FeedProps> = ({
  bannerList: _bannerList,
  inspAssets,
  handleDetail,
  hasMore,
  onLoadMore,
  isLoading,
  getBannerListLoading,
  active,
  handleVote,
  handleUnVote,
  referenceList,
  onExpose,
  observedIndicesUpdateDeeps,
  hideEmptyVoteCount = false,
  showSelected = false,
  hideUser = false,
  emptyListText,
  emptyEle,
  scrollElementId,
  columnsPreset,
  openRecreateInNewTab,
  onRecreateStart,
  onUsePostReference,
  fallbackContainerClassName,
  deferEmptyUntilLoaded = false,
}) => {
  const defaultColumnsPreset = useMemo<'fewer' | 'default' | 'more'>(() => {
    return active === CommunityEnum.MediaAsset || active === CommunityEnum.MiniVideo ? 'default' : 'fewer';
  }, [active]);
  const effectivePreset = columnsPreset || defaultColumnsPreset;
  const { isMounted, scrollElement, masonryRef, cellPositioner, onResize, cache } = useMasonry({
    scrollElementId: scrollElementId || 'scrollableDiv',
    columnsPreset: effectivePreset,
  });
  const [cellLoadingStates, setCellLoadingStates] = useState<LoadingState>({});
  const pathname = usePathname();
  const t = useTranslations();

  const activeDataList = useMemo(() => {
    return active === CommunityEnum.PostReferences ? referenceList : inspAssets;
  }, [active, referenceList, inspAssets]);
  const loadCycleKey = useMemo(() => {
    return (observedIndicesUpdateDeeps || []).map(String).join('|');
  }, [observedIndicesUpdateDeeps]);
  const [loadedCycleKey, setLoadedCycleKey] = useState<string | null>(null);
  const loadCycleStateRef = useRef({ key: loadCycleKey, hasStartedLoading: false });

  const exposureRefs = useRef<HTMLDivElement[]>([]);
  const { initObservedIndices } = useExpose(
    (idx) => {
      const item = activeDataList[idx];
      // getCurrentItem 仅获取灵感、短片、教程类型的投稿，其他类型 currentItem 为空且不上报 submission_show
      const currentItem = getCurrentItem(item);
      if (currentItem) {
        reportEvent('submission_show', {
          submit_id: currentItem?.id || '',
          submit_type: TAB_TYPE_MAP[mapTypeToExploreTab(currentItem?.type)],
          show_position: pathname,
          index: idx,
        });
      }
      onExpose?.(idx);
    },
    exposureRefs.current,
    activeDataList,
    [0.1, 1],
  );

  useEffect(() => {
    initObservedIndices();
  }, observedIndicesUpdateDeeps);

  useEffect(() => {
    if (loadCycleStateRef.current.key !== loadCycleKey) {
      loadCycleStateRef.current = { key: loadCycleKey, hasStartedLoading: false };
      setLoadedCycleKey(null);
    }

    if (activeDataList.length > 0) {
      setLoadedCycleKey(loadCycleKey);
      return;
    }

    if (isLoading) {
      loadCycleStateRef.current.hasStartedLoading = true;
      return;
    }

    if (loadCycleStateRef.current.hasStartedLoading) {
      setLoadedCycleKey(loadCycleKey);
    }
  }, [isLoading, activeDataList.length, loadCycleKey]);

  // 修改加载更多处理函数
  const loadMoreRows = async () => {
    if (hasMore && !isLoading) {
      // 设置相关单元格的加载状态
      const startIndex = activeDataList.length;
      const endIndex = startIndex + 5; // 假设每次加载5个
      const newLoadingStates: Record<number, boolean> = {};
      for (let i = startIndex; i <= endIndex; i++) {
        newLoadingStates[i] = true;
      }
      setCellLoadingStates((prev) => ({ ...prev, ...newLoadingStates }));

      try {
        await onLoadMore();
      } finally {
        // 加载完成后清除加载状态
        setCellLoadingStates((prev) => {
          const newState = { ...prev };
          for (let i = startIndex; i <= endIndex; i++) {
            delete newState[i];
          }
          return newState;
        });
      }
    }
  };

  const { getCardInfo } = useFeedCard({
    handleDetail,
    handleVote,
    handleUnVote,
    openRecreateInNewTab,
    onRecreateStart,
  });

  const cellRenderer = ({ index, key, parent, style }: MasonryCellProps) => {
    // 创建唯一的 key
    const cellKey = `${String(key)}-${index}`;

    // 获取实际的资产索引
    const itemIndex = index;
    const item = activeDataList[itemIndex];

    // 如果 item 为空或者正在加载中，显示骨架屏
    if (!item || cellLoadingStates[index]) {
      return (
        <CellMeasurer cache={cache.current} index={index} key={cellKey} parent={parent}>
          <div style={style}>
            <div className="relative mb-1 w-full">
              <div className="aspect-video w-full animate-pulse rounded-8 bg-gray-300/40" />
            </div>
          </div>
        </CellMeasurer>
      );
    }

    return (
      <CellMeasurer cache={cache.current} index={index} key={cellKey} parent={parent}>
        <div style={style} data-index={itemIndex}>
          <div
            ref={(el: HTMLDivElement | null) => {
              if (el) {
                exposureRefs.current[index] = el;
              }
            }}
          >
            {/* 主体 */}
            {active === CommunityEnum.PostReferences ? (
              <PostReferenceCard
                data={item as PostReferenceItem}
                handleDetail={handleDetail}
                onUse={onUsePostReference}
              />
            ) : (
              // 灵感短片教程
              <FeedCard
                item={item}
                {...{
                  ...getCardInfo({ item, hideUser }),
                  ...(showSelected
                    ? {
                        tag: (item as NewShowCaseList[number]).selected
                          ? t('creations.videoCard.officialSelected')
                          : '',
                        tooltipContent: t('creations.videoCard.officialSelectedTooltip'),
                      }
                    : {}),
                }}
                forceVideoAspectRatio={[CommunityEnum.ShortFilm, CommunityEnum.Tutorial].includes(active)}
                limitAspectRatio={true}
                hideEmptyVoteCount={hideEmptyVoteCount}
              />
            )}
          </div>
        </div>
      </CellMeasurer>
    );
  };

  // 请求数据时，显示loading
  if (
    !isMounted ||
    !scrollElement ||
    (!activeDataList.length && (isLoading || (deferEmptyUntilLoaded && loadedCycleKey !== loadCycleKey))) ||
    getBannerListLoading
  ) {
    return (
      <div className={cn('flex size-full flex-1 items-center justify-center', fallbackContainerClassName)}>
        <Loader />
      </div>
    );
  }

  if (!activeDataList.length) {
    return (
      <div className={cn('flex size-full flex-1 items-center justify-center', fallbackContainerClassName)}>
        {emptyEle ?? (
          <div className="flex h-full min-h-[400px] w-[400px] flex-col items-center justify-center lg:w-[320px]">
            <img
              src={CreateGuide.src}
              alt="create_guide"
              width={400}
              height={277}
              className="mb-8 h-auto w-[400px] opacity-60 sm:w-[200px] lg:mb-6 lg:w-[320px]"
            />
            <div className="mb-10 max-w-[300px] whitespace-pre text-center">
              {emptyListText || t('home.explore.empty')}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <WindowScroller scrollElement={scrollElement}>
        {({ height, scrollTop }) => (
          <InfiniteLoader
            isRowLoaded={isRowLoaded(activeDataList as NewShowCaseList)}
            loadMoreRows={loadMoreRows}
            rowCount={hasMore ? activeDataList.length + 1 : activeDataList.length}
            threshold={5}
          >
            {({ onRowsRendered }) => (
              <AutoSizer disableHeight onResize={onResize}>
                {({ width }) => {
                  if (!cellPositioner.current) {
                    cellPositioner.current = createPositioner({
                      width,
                      cache: cache.current,
                      columnsPreset: effectivePreset,
                    });
                  }
                  const cellCount = hasMore ? activeDataList.length + 1 : activeDataList.length;
                  return (
                    <VirtualizedMasonry
                      autoHeight
                      ref={masonryRef}
                      cellCount={cellCount}
                      cellMeasurerCache={cache.current}
                      cellPositioner={cellPositioner.current}
                      cellRenderer={cellRenderer}
                      height={height}
                      width={width}
                      className={cn('pb-28', style.virtualize)}
                      overscanByPixels={400}
                      scrollTop={scrollTop}
                      key={`masonry-${width}`}
                      onCellsRendered={({ stopIndex }) => {
                        onRowsRendered({
                          startIndex: 0,
                          stopIndex,
                        });
                      }}
                    />
                  );
                }}
              </AutoSizer>
            )}
          </InfiniteLoader>
        )}
      </WindowScroller>
      {!hasMore && !isLoading && (
        <div className="flex items-center justify-center gap-3 pb-10 text-sm text-system-white32">
          <span className="h-px w-16 bg-system-white16" />
          {t('home.explore.reachedBottom')}
          <span className="h-px w-16 bg-system-white16" />
        </div>
      )}
    </>
  );
};
